import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Box, Center, Heading, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import useInterval from 'use-interval';

import commonLocales from 'common/bankAccount/locales';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import BackButton from 'components/BackButton';
import { PICKABLE_ILLUSTRATIONS } from 'components/BankAccountIcon/constants';
import * as Layout from 'layouts/ContentLayout';

import BankAccountIllustrationPicker from '../BankAccountIllustrationPicker';
import {
  BankAccountFormSchema,
  getBankAccountFormSchema,
} from './bankAccountForm.schema';
import locales from './locales';

interface BankAccountFormProps {
  defaultValues?: DefaultValues<BankAccountFormSchema>;
  loading?: boolean;
  onBackButtonClick?: () => void;
  onSubmit: SubmitHandler<BankAccountFormSchema>;
}

const ANIMATION_WALLET_NAMES = locales.placeholders;

const ANIMATION_PROPS = {
  animate: { opacity: 1 },
  initial: { opacity: 0 },
  transition: { duration: 0.3, ease: 'easeOut' },
};
export type AnimationPropsType = typeof ANIMATION_PROPS;

const BankAccountForm: FC<BankAccountFormProps> = ({
  defaultValues,
  loading,
  onBackButtonClick,
  onSubmit,
}) => {
  const animationIllustrationNames = useMemo(
    () => PICKABLE_ILLUSTRATIONS.sort(() => Math.random() - 0.5).slice(0, 3),
    [],
  );
  const [animationIndex, setAnimationIndex] = useState(0);
  const [showSurroundingBorder, setShowSurroundingBorder] = useState(true);
  const { bankAccounts } = useCompanyBankAccounts();

  const otherBankAccounts = defaultValues
    ? bankAccounts.filter(
        (bankAccount) => bankAccount.name !== defaultValues.name,
      )
    : bankAccounts;

  const bankAccountNames = otherBankAccounts.map((bankAccount) =>
    bankAccount.name.trim().toLowerCase(),
  );

  const form = useForm<BankAccountFormSchema>({
    defaultValues: defaultValues || { illustrationName: '', name: '' },
    mode: 'onChange',
    resolver: zodResolver(
      getBankAccountFormSchema([
        ...bankAccountNames,
        commonLocales.mainAccount.trim().toLowerCase(),
      ]),
    ),
  });

  const isEdit = Boolean(defaultValues);
  const [fieldHasBeenTouched, setFieldHasBeenTouched] = useState(false);
  const shouldAnimate = !isEdit && !fieldHasBeenTouched;

  const setIllustrationFromAnimation = useCallback(() => {
    form.setValue(
      'illustrationName',
      animationIllustrationNames[animationIndex] as string,
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }, [animationIndex, form, animationIllustrationNames]);

  useInterval(
    () => {
      if (animationIndex === ANIMATION_WALLET_NAMES.length - 1) {
        setIllustrationFromAnimation();
        form.setFocus('name');
      } else {
        setAnimationIndex(animationIndex + 1);
      }
    },
    shouldAnimate ? 800 : null,
  );

  // this effect is used to set the illustrationName field value
  // when the animation has been interrupted by the user enterring a name
  // otherwise the illustration is displayed but not set as the field value
  // and if the user submits the form, the illustrationName field is empty
  useEffect(() => {
    if (
      !isEdit &&
      fieldHasBeenTouched &&
      !form.formState.dirtyFields.illustrationName
    ) {
      setIllustrationFromAnimation();
    }
  }, [
    form,
    shouldAnimate,
    setIllustrationFromAnimation,
    isEdit,
    fieldHasBeenTouched,
  ]);

  return (
    <Layout.Root>
      <Layout.Body>
        <Box marginBottom="space-16">
          <BackButton onClick={onBackButtonClick} />
        </Box>
        <Layout.BodyScrollableSection>
          <VStack alignItems="stretch" spacing="space-32">
            <Heading alignSelf="flex-start" size="lg" textAlign="left">
              {isEdit ? locales.updateTitle : locales.creationTitle}
            </Heading>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Box
                borderColor="grey.300"
                borderRadius="radius-8"
                borderWidth="1px"
                overflow="hidden"
              >
                <Controller
                  control={form.control}
                  name="illustrationName"
                  render={({ field }) => (
                    <BankAccountIllustrationPicker
                      animationIllustration={
                        animationIllustrationNames[animationIndex] as string
                      }
                      animationProps={ANIMATION_PROPS}
                      field={field}
                      setFieldHasBeenTouched={setFieldHasBeenTouched}
                    />
                  )}
                />
                <Box padding="space-24">
                  <Form.Field
                    error={form.formState.errors.name?.message}
                    label={locales.name}
                  >
                    <AnimatePresence initial={false} mode="wait">
                      {/* The animation uses the opacity property that animates all the input component (text and border)
                      To keep a stable border we put it in the surrounding div */}
                      <Box
                        borderColor={
                          showSurroundingBorder ? 'grey.300' : 'transparent'
                        }
                        borderRadius="radius-6"
                        borderStyle="solid"
                        borderWidth={1}
                      >
                        <motion.div {...ANIMATION_PROPS} key={animationIndex}>
                          <Controller
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <Form.Input
                                {...field}
                                borderColor="transparent"
                                // When it's focused, the Form.Input component sets a black border
                                // This prevents having the surrouding and the input border at the same time
                                onBlur={() => {
                                  setShowSurroundingBorder(true);
                                  field.onBlur();
                                }}
                                onFocus={() => {
                                  setShowSurroundingBorder(false);
                                  setFieldHasBeenTouched(true);
                                }}
                                placeholder={
                                  shouldAnimate
                                    ? ANIMATION_WALLET_NAMES[animationIndex]
                                    : locales.placeholder
                                }
                              />
                            )}
                          />
                        </motion.div>
                      </Box>
                    </AnimatePresence>
                  </Form.Field>
                </Box>
                <Center
                  borderColor="grey.300"
                  borderTopWidth="1px"
                  padding="space-24"
                >
                  <Button
                    isDisabled={loading || !form.formState.isDirty}
                    isExpanded
                    isLoading={loading}
                    type="submit"
                  >
                    {isEdit ? locales.update : locales.create}
                  </Button>
                </Center>
              </Box>
            </form>
          </VStack>
        </Layout.BodyScrollableSection>
      </Layout.Body>
    </Layout.Root>
  );
};

export default BankAccountForm;
